import { createStore, type StoreApi } from "zustand/vanilla";
import { type VaultTreeNode } from "./types.ts";
import { type VaultAdapter } from "../vault/VaultAdapter.ts";

/**
 * Vault-level UI state: which vault is open, its file tree, and the active
 * document. The canvas/markdown content lives in their own stores/views; this
 * only tracks the shell. The Sidebar subscribes to re-render the tree.
 */
export interface VaultState {
  /** Null until the user opens a folder. */
  adapter: VaultAdapter | null;
  name: string;
  tree: VaultTreeNode[];
  /** Vault-relative path of the open document, or null. */
  activePath: string | null;
  /**
   * Lo que está abierto en el panel lateral, o null.
   *
   * Va aparte de `activePath` y no lo reemplaza: el rail los marca distinto, y
   * sólo el principal decide qué carpetas se abren y dónde reabre el vault.
   */
  peekPath: string | null;
  /** Atajos del rail. Se cargan del registro del vault al abrirlo. */
  pinned: string[];
  /** Más reciente primero. */
  recents: string[];

  setVault: (adapter: VaultAdapter) => void;
  closeVault: () => void;
  setTree: (tree: VaultTreeNode[]) => void;
  setActive: (path: string | null) => void;
  setPeekPath: (path: string | null) => void;
  setPinned: (paths: string[]) => void;
  setRecents: (paths: string[]) => void;
  /** Re-read the folder tree from disk into the store. */
  refreshTree: () => Promise<void>;
}

export function createVaultStore(): StoreApi<VaultState> {
  return createStore<VaultState>((set, get) => ({
    adapter: null,
    name: "",
    tree: [],
    activePath: null,
    peekPath: null,
    pinned: [],
    recents: [],

    setVault(adapter) {
      // Los atajos son de CADA vault: el que abre entra sin los del anterior, y
      // quien los tenga guardados los carga enseguida (ver `openVault`).
      set({
        adapter,
        name: adapter.name,
        tree: [],
        activePath: null,
        peekPath: null,
        pinned: [],
        recents: [],
      });
    },

    closeVault() {
      set({
        adapter: null,
        name: "",
        tree: [],
        activePath: null,
        peekPath: null,
        pinned: [],
        recents: [],
      });
    },

    setTree(tree) {
      set({ tree });
    },

    setActive(path) {
      set({ activePath: path });
    },

    setPeekPath(path) {
      set({ peekPath: path });
    },

    setPinned(paths) {
      set({ pinned: paths });
    },

    setRecents(paths) {
      set({ recents: paths });
    },

    async refreshTree() {
      const { adapter } = get();
      if (!adapter) return;
      set({ tree: await adapter.tree() });
    },
  }));
}
